import { useState } from 'react';
import { Link } from 'react-router';
import styled from 'styled-components';
import Dropdown from 'react-bootstrap/Dropdown';

import {
	AiOutlineFileText,
	AiOutlineMore,
	AiFillFolderOpen,
	AiOutlinePlayCircle,
	AiOutlineDownload,
	AiOutlineFilePdf,
} from 'react-icons/ai';
import { FaRegMap } from 'react-icons/fa';

import { archiveProject, adminGetProjectFilesDowloadUrl, unsetDemoProject } from '@utils/api';
import { downloadProjectFiles } from '@utils/downloads';
import { IMAGE_MODE } from '@utils/constants';

import ReportModal from './ReportModal';
import DetailsModal from './DetailsModal';
import OdmPostprocessModal from './OdmPostprocessModal';
import SagemakerDownloadModal from './SagemakerDownloadModal';
import DemoProjectModal from './DemoProjectModal';
import CopyModal from './CopyModal';
import EditModal from './EditModal';
import { useToast } from '@contexts/Toast.context';
import { useConfirm } from '@contexts/Confirm.context';

const DropdownToggle = styled(Dropdown.Toggle)`
	background: none;
	padding: 0;
	font-size: 1.2em;
	&.dropdown-toggle::after {
		display: none;
	}
`;

const DropdownMenu = ({ data }) => {
	const { addToast } = useToast();
	const { confirm } = useConfirm();
	const iconClass = 'mt-1 mb-2 me-2';

	const { uuid, image_mode, demo_project, title } = data;
	
	const isVideoProject = image_mode === IMAGE_MODE.VIDEO;
	const projectPath = isVideoProject ? `/video-projects/${uuid}` : `/projects/${uuid}`;

	const [showReport, setShowReport] = useState(false);
	const [showDetails, setShowDetails] = useState(null);
	const [showPostprocess, setShowPostprocess] = useState(false);
	const [showSagemaker, setShowSagemaker] = useState(false);
	const [showProjectDemo, setShowProjectDemo] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);

	const handleDelete = async () => {
		const userConfirmed = await confirm({
			title: 'Archive Project',
			message: (
				<>
					Are you sure you want to archive this project: <strong>{title}</strong>?
				</>
			),
		});

		if (userConfirmed) {
			try {
				await archiveProject(uuid);
				window.location.reload();
			} catch (err) {
				console.error('Failed to archive project', err);
				addToast({
					id: 'project-archive-error',
					bg: 'danger',
					title: 'Failed to archive project.',
				});
			}
		}
	};

	const handleUnsetDemo = async () => {
		const userConfirmed = await confirm({
			title: 'Unset Demo Project',
			message: (
				<>
					Are you sure you want to unset <strong>{title}</strong> as a demo project?
				</>
			),
		});

		if (userConfirmed) {
			try {
				await unsetDemoProject(uuid);
				window.location.reload();
			} catch (err) {
				console.error('Failed to unset demo project', err);
				addToast({
					id: 'demo-project-unset-error',
					bg: 'danger',
					title: 'Failed to unset demo project.',
				});
			}
		}
	};

	const handleDownloadImages = async () => {
		await downloadProjectFiles(uuid)
			.then(response => {
				addToast({
					id: 'download-images-success',
					bg: 'success',
					title: response,
				});
			})
			.catch(err => {
				addToast({
					id: 'download-images-error',
					bg: 'danger',
					title: err,
				});
			});
	};

	const presignedUrl = async (name, path, file_name) => {
		await adminGetProjectFilesDowloadUrl(uuid, path, file_name)
			.then(response => {
				const url = Object.values(response)[0]; // Get the first value from the response object
				window.open(url, '_blank'); // Open the URL in a new tab
			})
			.catch(err => {
				console.error('Failed getting project files for download', err);
				addToast({
					id: 'download-files-error',
					bg: 'danger',
					title: `Failed to get ${name}. Check the console for more information.`,
				});
			});
	};

	return (
		<>
			<Dropdown>
				<DropdownToggle variant="dark">
					<AiOutlineMore />
				</DropdownToggle>
				<Dropdown.Menu variant="dark">
					<Dropdown.Item as="button" type="button" onClick={() => setShowReport(true)}>
						<AiOutlineFileText className={iconClass} aria-hidden="true" />
						Resource report
					</Dropdown.Item>
					<Dropdown.Item as={Link} to={projectPath}>
						<FaRegMap className={iconClass} aria-hidden="true" />
						View on map
					</Dropdown.Item>
					<Dropdown.Item
						as="button"
						type="button"
						onClick={() => setShowDetails('project')}>
						<AiOutlineFileText className={iconClass} aria-hidden="true" />
						Details
					</Dropdown.Item>

					<Dropdown.Divider className="border-secondary" />

					<Dropdown.Item
						as="button"
						type="button"
						onClick={() => {
							console.log('setShowModal');
							setShowEditModal('project');
						}}>
						<AiOutlineFileText className={iconClass} aria-hidden="true" />
						Edit project
					</Dropdown.Item>

					<Dropdown.Item as="button" type="button" onClick={handleDownloadImages}>
						<AiOutlineDownload className={iconClass} aria-hidden="true" />
						Images from s3
					</Dropdown.Item>

					{(image_mode === IMAGE_MODE.GENERATE_ORTHOPHOTO ||
						image_mode === IMAGE_MODE.UPLOAD_ORTHOPHOTO) && (
						<>
							<Dropdown.Item
								as="button"
								type="button"
								onClick={() =>
									presignedUrl('ODM report', 'odm_report/', 'report.pdf')
								}>
								<AiOutlineFilePdf className={iconClass} aria-hidden="true" />
								ODM report
							</Dropdown.Item>

							<Dropdown.Item
								as="button"
								type="button"
								onClick={() => presignedUrl('ODM log', '', 'log.json')}>
								<AiOutlineFileText className={iconClass} aria-hidden="true" />
								ODM log
							</Dropdown.Item>
						</>
					)}

					<Dropdown.Item as="button" type="button" onClick={() => setShowSagemaker(true)}>
						<AiOutlineFileText className={iconClass} aria-hidden="true" />
						Sagemaker files
					</Dropdown.Item>

					<Dropdown.Divider className="border-secondary" />
					<Dropdown.Item
						as="button"
						type="button"
						onClick={() => setShowProjectDemo(true)}>
						<AiOutlinePlayCircle className={iconClass} aria-hidden="true" />
						{demo_project ? 'Update demo model' : 'Set as demo project'}
					</Dropdown.Item>
					{demo_project && (
						<Dropdown.Item as="button" type="button" onClick={() => handleUnsetDemo()}>
							<AiOutlinePlayCircle className={iconClass} aria-hidden="true" />
							Unset as demo project
						</Dropdown.Item>
					)}

					<Dropdown.Divider className="border-secondary" />

					{image_mode === IMAGE_MODE.GENERATE_ORTHOPHOTO && (
						<Dropdown.Item
							as="button"
							type="button"
							onClick={() => setShowPostprocess(true)}>
							<AiOutlinePlayCircle className={iconClass} aria-hidden="true" />
							ODM Postprocess
						</Dropdown.Item>
					)}

					<Dropdown.Item as="button" type="button" onClick={handleDelete}>
						<AiFillFolderOpen className={iconClass} aria-hidden="true" />
						Archive
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>

			{showReport && (
				<ReportModal project_uuid={uuid} show={showReport} setShow={setShowReport} />
			)}

			{showDetails && (
				<DetailsModal project_uuid={uuid} type={showDetails} setType={setShowDetails} />
			)}

			{showPostprocess && (
				<OdmPostprocessModal
					project_uuid={uuid}
					image_mode={image_mode}
					show={showPostprocess}
					setShow={setShowPostprocess}
				/>
			)}

			{showSagemaker && (
				<SagemakerDownloadModal
					project_uuid={uuid}
					show={showSagemaker}
					setShow={setShowSagemaker}
				/>
			)}

			{showProjectDemo && (
				<DemoProjectModal
					project={data}
					show={showProjectDemo}
					setShow={setShowProjectDemo}
				/>
			)}

			{showEditModal && (
				<EditModal project={data} show={showEditModal} setShow={setShowEditModal} />
			)}
		</>
	);
};
export default DropdownMenu;
